/**
 * HTML => plain text conversion for the diary bulk export (PDF and
 * Markdown). Entry texts come from the Tiptap editor and contain only
 * a small set of tags (p, blockquote, br) plus HTML entities.
 *
 * Deliberately self-contained: the existing single-entry printer
 * (DiaryPrinter.js) keeps its own conversion and is not touched.
 */

const ENTITIES = {
	"&Uuml;": "Ü",
	"&uuml;": "ü",
	"&Ouml;": "Ö",
	"&ouml;": "ö",
	"&Auml;": "Ä",
	"&auml;": "ä",
	"&szlig;": "ß",
	"&nbsp;": " ",
	"&lt;": "<",
	"&gt;": ">",
	"&quot;": "\"",
	"&#39;": "'",
	"&amp;": "&", // last, so decoded entities are not re-decoded
};

function decodeEntities(text) {
	for (const entity of Object.keys(ENTITIES)) {
		text = text.split(entity).join(ENTITIES[entity]);
	}
	return text;
}

function stripTags(html) {
	const text = html
		.replace(/<\/p>/gi, "\n\n")
		.replace(/<[^>]+>/g, "");
	return decodeEntities(text);
}

/**
 * One-line preview / search text: tags become spaces, entities are
 * decoded, whitespace collapses. No block handling, unlike
 * ``htmlToPlainText``.
 */
export function htmlInlineText(html) {
	if (!html) { return ""; }
	return decodeEntities(String(html).replace(/<\/?[^>]+(>|$)/g, " "))
		.replace(/\s+/g, " ").trim();
}

/**
 * Convert an entry's HTML text to plain text.
 *
 * options.quotePrefix: prefix for lines inside <blockquote> blocks.
 * The Markdown export passes "> "; the PDF export uses the default
 * (no prefix).
 */
export function htmlToPlainText(html, options = {}) {
	if (!html) { return ""; }
	const quotePrefix = options.quotePrefix || "";

	let text = String(html)
		.replace(/\r\n/g, "\n")
		.replace(/<br\s*\/?>/gi, "\n");

	text = text.replace(
		/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi,
		(match, inner) => {
			const lines = stripTags(inner)
				.split("\n")
				.map((line) => line.trim())
				.filter((line) => line.length > 0)
				.map((line) => quotePrefix + line);
			return "\n\n" + lines.join("\n") + "\n\n";
		}
	);

	text = stripTags(text);

	return text
		.split("\n")
		.map((line) => line.replace(/[ \t]+$/g, ""))
		.join("\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}
